import Heading from 'LuxKit/Typography/Heading'
import { useContext } from 'react'
import { useIntl } from 'react-intl'
import { useWhyBookWithUsContent } from './constants'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import styled from 'styled-components'
import LayoutContainer from 'components/Common/LayoutContainer'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { connect } from 'react-redux'
import ContactContext from 'contexts/contactContext'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'

const StyledLayoutContainer = styled(LayoutContainer)`
  display: flex;
  flex-direction: column;
  gap: ${rem(40)};
  padding-block: ${rem(40)};
  padding-inline: ${rem(20)} 0;

  ${mediaQueryUp.tablet} {
    padding-block: ${rem(40)};
    padding-inline: ${rem(16)};
  }

  ${mediaQueryUp.largeDesktop} {
    padding-block: ${rem(40)};
    padding-inline: unset;
  }
`

const TileContainer = styled.div`
  display: flex;
  gap: ${rem(12)};
  width: 70vw;

  ${mediaQueryUp.tablet} {
    flex-direction: column;
    width: unset;
  }
`

const GridContainer = styled.div`
  display: grid;
  grid-template-rows: 1fr 1fr 1fr;
  row-gap: ${rem(24)};
  column-gap: ${rem(16)};
  grid-auto-flow: column;
  overflow-x: auto;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */

  ${mediaQueryUp.tablet} {
    gap: ${rem(24)};
    grid-auto-flow: row;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr;
  }

  ${mediaQueryUp.largeDesktop} {
    gap: ${rem(24)};
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr;
  }
`

interface Props {
  regionCode: string
}

function WhyBookWithUsSection(props: Props) {
  const intl = useIntl()
  const { regionCode } = props
  const geoContact = useContext(ContactContext)
  const whyBookContent = useWhyBookWithUsContent()

  const sectionHeading = intl.formatMessage({
    id: 'TNjx9m6bVxDalLCauUnU',
    defaultMessage: 'Why book with Luxury Escapes?',
    description: 'About us page - why book with us section heading on the about us page',
  })

  const customerSupportHeading = intl.formatMessage({
    id: '3zHhg8mblesJNai-6JPK',
    defaultMessage: 'Customer Support',
    description: 'Why book with us - customer support heading for regions without 24/7 support',
  })

  const customerSupportBody = intl.formatMessage({
    id: 'mIeNSUQLJNIfgnJu5LQF',
    defaultMessage: 'Available seven days a week from 9am to 7pm, ensuring support when you need it most.',
    description: 'Why book with us - customer support body text for regions without 24/7 support',
  })

  return (
    <Pane>
      <StyledLayoutContainer>
        <Heading variant="heading2">{sectionHeading}</Heading>
        <GridContainer>
          {whyBookContent.map((content) => {
            const {
              key,
              Icon,
              heading: propsHeading,
              body: propsBody,
              link,
            } = content
            let heading = propsHeading ?? ''
            let body = propsBody ?? ''
            const isCustomerServiceItem = key === 'customer-service'
            const linkText = isCustomerServiceItem ? geoContact.local.humanReadable : link?.text ?? undefined
            const linkHref = isCustomerServiceItem ? `tel:${geoContact.local.number}` : link?.href

            if (isCustomerServiceItem && NO_24_7_SUPPORT_REGIONS.includes(regionCode)) {
              heading = customerSupportHeading
              body = customerSupportBody
            }

            return (
              <TileContainer key={key}>
                {Icon}
                <Group direction="vertical" gap={12}>
                  <Group direction="vertical" gap={4}>
                    <BodyText variant="medium" weight="bold">
                      {heading}
                    </BodyText>
                    <BodyText variant="medium">
                      {body}
                    </BodyText>
                  </Group>
                  {linkText && (
                    <TextLink to={link?.to} href={linkHref} size="small" weight="bold" target="_blank">
                      {linkText}
                    </TextLink>
                  )}
                </Group>
              </TileContainer>
            ) })}
        </GridContainer>
      </StyledLayoutContainer>
    </Pane>
  )
}

function mapStateToProps(state: App.State) {
  return {
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(WhyBookWithUsSection)
